/**
 * ReportBuilder — Custom report assembly tool for leadership.
 *
 * Lets a user pick:
 *   1. Scope: org-wide OR one-or-more specific departments
 *   2. Sections: any combination of the existing analytical lenses
 *   3. Title + optional intro paragraph
 *
 * Then assembles a single scrollable report that can be exported as PDF.
 *
 * Reuses existing section components (HeadlinesCard, RetentionRiskView,
 * AutomationOutsourceView, StrategicTimelineView, OrgDesignView) by
 * passing them the appropriate sentimentReport / orgReport prop. Sections
 * gracefully no-op when their data isn't available.
 */
import React, { useState, useRef } from 'react';
import html2pdf from 'html2pdf.js';
import {
    FileText, Building2, Users, Check, Loader2, Settings,
    ChevronRight, Download, Hourglass, Globe, Target,
    Heart, Zap, Calendar, MessageSquare, Pin, Sparkles,
    AlertTriangle, ArrowLeft
} from 'lucide-react';
import { API_BASE_URL } from '../../services/api';
import HeadlinesCard from './HeadlinesCard';
import RetentionRiskView from './RetentionRiskView';
import AutomationOutsourceView from './AutomationOutsourceView';
import StrategicTimelineView from './StrategicTimelineView';
import OrgDesignView from './OrgDesignView';
import PulseFeedbackView from './PulseFeedbackView';

const getAuthHeaders = () => {
    const token = localStorage.getItem('adminToken');
    const swaPrincipal = localStorage.getItem('swaPrincipal');
    return {
        'Content-Type': 'application/json',
        ...(token ? { Authorization: `Bearer ${token}` } : {}),
        ...(swaPrincipal ? { 'X-MS-CLIENT-PRINCIPAL': swaPrincipal } : {}),
    };
};

// Section catalog. `levels` controls which sections show for which scope.
const SECTION_CATALOG = [
    {
        id: 'headlines',
        label: 'Headlines (TLDR)',
        description: 'Top 5 takeaways: sentiment, friction, request, risk, headline recommendation.',
        icon: <Pin size={16} />,
        levels: ['org', 'department']
    },
    {
        id: 'executive_summary',
        label: 'Executive Summary',
        description: 'AI-written 2-3 sentence overview at the top of the report.',
        icon: <FileText size={16} />,
        levels: ['org', 'department']
    },
    {
        id: 'sentiment',
        label: 'Team Sentiment',
        description: 'Morale, workload, and team-health indicators.',
        icon: <Heart size={16} />,
        levels: ['department']
    },
    {
        id: 'recommendations',
        label: 'Strategic Recommendations',
        description: 'AI-generated recommendations with charter pillar tagging.',
        icon: <Target size={16} />,
        levels: ['org', 'department']
    },
    {
        id: 'automation',
        label: 'Automation & Outsource',
        description: 'AI/automation opportunities and outsource candidates.',
        icon: <Zap size={16} />,
        levels: ['org', 'department']
    },
    {
        id: 'timeline',
        label: 'Strategic Timeline',
        description: 'Phased roadmap aligned to charter horizons (0-6mo / 6-12mo / 1-2y / 3-5y).',
        icon: <Calendar size={16} />,
        levels: ['org', 'department']
    },
    {
        id: 'retention',
        label: 'Retention Risk',
        description: 'Flight risk signals and retention drivers.',
        icon: <AlertTriangle size={16} />,
        levels: ['org']
    },
    {
        id: 'orgdesign',
        label: 'Org Design',
        description: 'Structural recommendations and capability gaps. Org-level only.',
        icon: <Users size={16} />,
        levels: ['org']
    },
    {
        id: 'pulse',
        label: 'Pulse Feedback',
        description: 'Employee validation feedback for distributed reports.',
        icon: <MessageSquare size={16} />,
        levels: ['org', 'department']
    }
];

const DEFAULT_SECTIONS = ['headlines', 'executive_summary', 'recommendations', 'automation', 'timeline'];

export default function ReportBuilder({ departments, orgReport, loadOrgReport }) {
    // Wizard step: 'configure' or 'preview'
    const [step, setStep] = useState('configure');

    // Configuration state
    const [scope, setScope] = useState('org'); // 'org' or 'departments'
    const [selectedDepartments, setSelectedDepartments] = useState([]);
    const [selectedSections, setSelectedSections] = useState(new Set(DEFAULT_SECTIONS));
    const [reportTitle, setReportTitle] = useState('Custom Leadership Report');
    const [reportIntro, setReportIntro] = useState('');

    // Loaded report data (cache by department key)
    const [deptReports, setDeptReports] = useState({}); // { [deptName]: sentimentReport }
    const [loadingScope, setLoadingScope] = useState(false);
    const [loadProgress, setLoadProgress] = useState('');
    const [loadError, setLoadError] = useState(null);

    // Export state
    const [exporting, setExporting] = useState(false);
    const reportRef = useRef(null);

    // ---- Configure step actions ----

    const toggleSection = (id) => {
        setSelectedSections((prev) => {
            const next = new Set(prev);
            if (next.has(id)) next.delete(id);
            else next.add(id);
            return next;
        });
    };

    const toggleDepartment = (dept) => {
        setSelectedDepartments((prev) =>
            prev.includes(dept) ? prev.filter((d) => d !== dept) : [...prev, dept]
        );
    };

    const visibleSections = SECTION_CATALOG.filter((s) =>
        scope === 'org' ? s.levels.includes('org') : s.levels.includes('department')
    );

    const canBuild =
        selectedSections.size > 0 &&
        (scope === 'org' || selectedDepartments.length > 0);

    // ---- Build action: load required data, then switch to preview ----

    const fetchDeptSentiment = async (dept) => {
        // Tries to load existing cached sentiment report. Does NOT trigger
        // generation — builder is for assembling existing reports. If a
        // department has no report yet, that section will show its own
        // "no data" empty state.
        const url = `${API_BASE_URL}/api/admin/leadership/department/${encodeURIComponent(dept)}/sentiment/status`;
        try {
            const res = await fetch(url, { headers: getAuthHeaders() });
            if (!res.ok) return null;
            const data = await res.json();
            if (data.status === 'completed' && data.result) return data.result;
            return null;
        } catch {
            return null;
        }
    };

    const handleBuild = async () => {
        setLoadError(null);
        setLoadingScope(true);
        try {
            if (scope === 'org') {
                if (!orgReport && loadOrgReport) {
                    setLoadProgress('Loading organization report…');
                    await loadOrgReport(false);
                }
            } else {
                const next = { ...deptReports };
                for (const dept of selectedDepartments) {
                    if (!next[dept]) {
                        setLoadProgress(`Loading ${dept} report…`);
                        const report = await fetchDeptSentiment(dept);
                        next[dept] = report; // may be null — section will render its own empty state
                    }
                }
                setDeptReports(next);
            }
            setStep('preview');
        } catch (err) {
            setLoadError(err.message || 'Failed to load report data.');
        } finally {
            setLoadingScope(false);
            setLoadProgress('');
        }
    };

    const handleEditConfig = () => setStep('configure');

    const handleExportPdf = async () => {
        if (!reportRef.current) return;
        setExporting(true);
        try {
            const safeTitle = (reportTitle || 'Custom-Report').replace(/[^a-z0-9]+/gi, '-');
            const filename = `${safeTitle}-${new Date().toISOString().split('T')[0]}.pdf`;

            const options = {
                margin: [0.5, 0.5, 0.5, 0.5],
                filename,
                image: { type: 'jpeg', quality: 0.95 },
                html2canvas: {
                    scale: 2,
                    useCORS: true,
                    letterRendering: true,
                    scrollX: 0,
                    scrollY: -window.scrollY,
                    backgroundColor: '#ffffff',
                    onclone: (doc) => {
                        doc.querySelectorAll('.no-print').forEach((el) => el.remove());
                    }
                },
                jsPDF: { unit: 'in', format: 'letter', orientation: 'portrait', compress: true },
                pagebreak: { mode: ['css', 'legacy'], avoid: ['.rb-section', '.headlines-card', '.rp-section'] }
            };

            await html2pdf().set(options).from(reportRef.current).save();
        } catch (err) {
            console.error('PDF export failed:', err);
            alert('PDF export failed. Try again.');
        } finally {
            setExporting(false);
        }
    };

    // ===========================================================
    // CONFIGURE STEP
    // ===========================================================
    if (step === 'configure') {
        return (
            <div className="report-builder">
                <div className="rb-header">
                    <h2>
                        <Sparkles size={18} /> Report Builder
                    </h2>
                    <p className="rb-subtitle">
                        Pick a scope and the sections you want, then assemble a custom leadership report.
                    </p>
                </div>

                {/* Step 1: Title */}
                <div className="rb-config-card">
                    <div className="rb-config-card-header">
                        <span className="rb-step-num">1</span>
                        <h3>Title &amp; intro</h3>
                    </div>
                    <div className="rb-config-card-body">
                        <label className="rb-field">
                            <span className="rb-field-label">Report title</span>
                            <input
                                type="text"
                                className="rb-input"
                                value={reportTitle}
                                onChange={(e) => setReportTitle(e.target.value)}
                                placeholder="Custom Leadership Report"
                            />
                        </label>
                        <label className="rb-field">
                            <span className="rb-field-label">
                                Intro paragraph <span className="rb-field-hint">(optional)</span>
                            </span>
                            <textarea
                                className="rb-input rb-textarea"
                                value={reportIntro}
                                onChange={(e) => setReportIntro(e.target.value)}
                                placeholder="E.g. Briefing for the April leadership meeting…"
                                rows={3}
                            />
                        </label>
                    </div>
                </div>

                {/* Step 2: Scope */}
                <div className="rb-config-card">
                    <div className="rb-config-card-header">
                        <span className="rb-step-num">2</span>
                        <h3>Scope</h3>
                    </div>
                    <div className="rb-config-card-body">
                        <div className="rb-scope-toggle">
                            <button
                                type="button"
                                className={`rb-scope-btn ${scope === 'org' ? 'active' : ''}`}
                                onClick={() => setScope('org')}
                            >
                                <Globe size={16} />
                                <div>
                                    <strong>Organization-wide</strong>
                                    <span>Cross-department comparison &amp; org-level lenses</span>
                                </div>
                            </button>
                            <button
                                type="button"
                                className={`rb-scope-btn ${scope === 'departments' ? 'active' : ''}`}
                                onClick={() => setScope('departments')}
                            >
                                <Building2 size={16} />
                                <div>
                                    <strong>Specific departments</strong>
                                    <span>Pick one or more departments to assemble side-by-side</span>
                                </div>
                            </button>
                        </div>

                        {scope === 'departments' && (
                            <div className="rb-dept-picker">
                                <p className="rb-dept-picker-label">
                                    Select departments to include ({selectedDepartments.length} selected):
                                </p>
                                <div className="rb-dept-grid">
                                    {departments.map((d) => {
                                        const checked = selectedDepartments.includes(d);
                                        return (
                                            <button
                                                type="button"
                                                key={d}
                                                className={`rb-dept-chip ${checked ? 'checked' : ''}`}
                                                onClick={() => toggleDepartment(d)}
                                            >
                                                {checked ? <Check size={14} /> : <span className="rb-dept-chip-empty" />}
                                                <span>{d}</span>
                                            </button>
                                        );
                                    })}
                                </div>
                            </div>
                        )}
                    </div>
                </div>

                {/* Step 3: Sections */}
                <div className="rb-config-card">
                    <div className="rb-config-card-header">
                        <span className="rb-step-num">3</span>
                        <h3>Sections to include</h3>
                        <span className="rb-section-count">
                            {selectedSections.size} of {visibleSections.length} selected
                        </span>
                    </div>
                    <div className="rb-config-card-body">
                        <div className="rb-section-grid">
                            {visibleSections.map((s) => {
                                const checked = selectedSections.has(s.id);
                                return (
                                    <button
                                        type="button"
                                        key={s.id}
                                        className={`rb-section-card ${checked ? 'checked' : ''}`}
                                        onClick={() => toggleSection(s.id)}
                                    >
                                        <div className="rb-section-card-header">
                                            <span className="rb-section-card-icon">{s.icon}</span>
                                            <span className="rb-section-card-label">{s.label}</span>
                                            <span className="rb-section-card-check">
                                                {checked ? <Check size={14} /> : null}
                                            </span>
                                        </div>
                                        <p className="rb-section-card-desc">{s.description}</p>
                                    </button>
                                );
                            })}
                        </div>
                    </div>
                </div>

                {loadError && <div className="rp-error">{loadError}</div>}

                {/* Build action */}
                <div className="rb-actions">
                    <button
                        type="button"
                        className="rp-btn rp-btn-primary rb-build-btn"
                        onClick={handleBuild}
                        disabled={!canBuild || loadingScope}
                    >
                        {loadingScope ? (
                            <>
                                <Loader2 size={14} className="spin" /> {loadProgress || 'Loading…'}
                            </>
                        ) : (
                            <>
                                <Sparkles size={14} /> Build report
                                <ChevronRight size={14} />
                            </>
                        )}
                    </button>
                    {!canBuild && !loadingScope && (
                        <p className="rb-help-text">
                            {selectedSections.size === 0
                                ? 'Select at least one section.'
                                : 'Select at least one department.'}
                        </p>
                    )}
                </div>
            </div>
        );
    }

    // ===========================================================
    // PREVIEW STEP — assembled report
    // ===========================================================
    return (
        <div className="report-builder">
            <div className="rb-preview-toolbar no-print">
                <button
                    type="button"
                    className="rp-btn rp-btn-secondary"
                    onClick={handleEditConfig}
                >
                    <ArrowLeft size={14} /> Edit configuration
                </button>
                <button
                    type="button"
                    className="rp-btn rp-btn-primary"
                    onClick={handleExportPdf}
                    disabled={exporting}
                >
                    {exporting ? (
                        <>
                            <Hourglass size={14} /> Generating PDF…
                        </>
                    ) : (
                        <>
                            <Download size={14} /> Export PDF
                        </>
                    )}
                </button>
            </div>

            <div className="rb-assembled" ref={reportRef}>
                <div className="rb-assembled-header">
                    <h1 className="rb-report-title">{reportTitle || 'Custom Leadership Report'}</h1>
                    <p className="rb-report-meta">
                        {scope === 'org'
                            ? 'Organization-wide'
                            : `${selectedDepartments.length} department${selectedDepartments.length === 1 ? '' : 's'}: ${selectedDepartments.join(', ')}`}
                        &nbsp;•&nbsp;
                        Generated {new Date().toLocaleDateString('en-US', {
                            year: 'numeric', month: 'long', day: 'numeric'
                        })}
                    </p>
                    {reportIntro && <p className="rb-report-intro">{reportIntro}</p>}
                </div>

                {scope === 'org' ? (
                    <AssembledScope
                        scopeLabel="Organization"
                        sentimentReport={null}
                        orgReport={orgReport}
                        isOrgLevel
                        sections={selectedSections}
                    />
                ) : (
                    selectedDepartments.map((dept) => (
                        <div key={dept} className="rb-dept-block">
                            <h2 className="rb-dept-block-title">
                                <Building2 size={18} /> {dept}
                            </h2>
                            <AssembledScope
                                scopeLabel={dept}
                                sentimentReport={deptReports[dept]}
                                orgReport={null}
                                isOrgLevel={false}
                                sections={selectedSections}
                                missingMessage={
                                    !deptReports[dept]
                                        ? 'No sentiment report found for this department yet. Generate one from the Department Sentiment tab first, then rebuild.'
                                        : null
                                }
                            />
                        </div>
                    ))
                )}
            </div>
        </div>
    );
}

/**
 * Renders the selected sections for one scope (org-wide OR a single department).
 */
function AssembledScope({
    scopeLabel,
    sentimentReport,
    orgReport,
    isOrgLevel,
    sections,
    missingMessage
}) {
    const analysis = isOrgLevel ? orgReport?.analysis : sentimentReport?.analysis;

    if (missingMessage) {
        return (
            <div className="rp-empty-state rb-empty-scope">
                <AlertTriangle size={20} />
                <p>{missingMessage}</p>
            </div>
        );
    }

    if (!analysis) {
        return (
            <div className="rp-empty-state rb-empty-scope">
                <AlertTriangle size={20} />
                <p>
                    No analysis available for {scopeLabel}.
                    {isOrgLevel
                        ? ' Generate the organization report first.'
                        : ' Generate a sentiment report for this department first.'}
                </p>
            </div>
        );
    }

    return (
        <>
            {sections.has('headlines') && (
                <div className="rb-section">
                    <HeadlinesCard analysis={analysis} summary={null} department={isOrgLevel ? null : scopeLabel} />
                </div>
            )}

            {sections.has('executive_summary') && analysis.executive_summary && (
                <div className="rb-section rb-exec-summary">
                    <h3>
                        <FileText size={16} /> Executive Summary
                    </h3>
                    <p>{analysis.executive_summary}</p>
                </div>
            )}

            {sections.has('sentiment') && !isOrgLevel && analysis.team_sentiment && (
                <div className="rb-section">
                    <h3>
                        <Heart size={16} /> Team Sentiment
                    </h3>
                    <SentimentSummary sentiment={analysis.team_sentiment} workload={analysis.workload_health} />
                </div>
            )}

            {sections.has('recommendations') && (
                <div className="rb-section">
                    <h3>
                        <Target size={16} /> Strategic Recommendations
                    </h3>
                    <RecommendationsList
                        recs={
                            isOrgLevel
                                ? analysis.strategic_priorities || analysis.strategic_recommendations
                                : analysis.strategic_recommendations
                        }
                    />
                </div>
            )}

            {sections.has('automation') && (
                <div className="rb-section">
                    <AutomationOutsourceView
                        sentimentReport={sentimentReport}
                        orgReport={orgReport}
                        isOrgLevel={isOrgLevel}
                        loading={false}
                        onRegenerate={null}
                    />
                </div>
            )}

            {sections.has('timeline') && (
                <div className="rb-section">
                    <StrategicTimelineView
                        sentimentReport={sentimentReport}
                        orgReport={orgReport}
                        isOrgLevel={isOrgLevel}
                        loading={false}
                        onRegenerate={null}
                    />
                </div>
            )}

            {sections.has('retention') && isOrgLevel && (
                <div className="rb-section">
                    <RetentionRiskView
                        sentimentReport={null}
                        orgReport={orgReport}
                        isOrgLevel
                        loading={false}
                        onRegenerate={null}
                    />
                </div>
            )}

            {sections.has('orgdesign') && isOrgLevel && (
                <div className="rb-section">
                    <OrgDesignView orgReport={orgReport} loading={false} onRegenerate={null} />
                </div>
            )}

            {sections.has('pulse') && (
                <div className="rb-section">
                    <PulseFeedbackView department={isOrgLevel ? null : scopeLabel} />
                </div>
            )}
        </>
    );
}

function SentimentSummary({ sentiment, workload }) {
    if (!sentiment) return null;
    return (
        <div className="rb-sentiment-grid">
            {sentiment.overall_assessment && (
                <div className="rb-sentiment-card">
                    <span className="rb-sentiment-label">Overall</span>
                    <span className="rb-sentiment-value">{sentiment.overall_assessment}</span>
                </div>
            )}
            {sentiment.morale_indicator && (
                <div className="rb-sentiment-card">
                    <span className="rb-sentiment-label">Morale</span>
                    <span className="rb-sentiment-value">{sentiment.morale_indicator}</span>
                </div>
            )}
            {workload?.burnout_risk_level && (
                <div className="rb-sentiment-card">
                    <span className="rb-sentiment-label">Burnout risk</span>
                    <span className="rb-sentiment-value">{workload.burnout_risk_level}</span>
                </div>
            )}
            {workload?.workload_balance && (
                <div className="rb-sentiment-card">
                    <span className="rb-sentiment-label">Workload balance</span>
                    <span className="rb-sentiment-value">{workload.workload_balance}</span>
                </div>
            )}
        </div>
    );
}

function RecommendationsList({ recs }) {
    if (!Array.isArray(recs) || recs.length === 0) {
        return <p className="rb-muted">No recommendations available in this report.</p>;
    }
    return (
        <ul className="rb-rec-list">
            {recs.slice(0, 8).map((r, idx) => (
                <li key={idx} className="rb-rec-item">
                    <div className="rb-rec-title">
                        {r.recommendation || r.priority || r.title || 'Recommendation'}
                        {r.pillar && <span className="rb-rec-pillar">{r.pillar.replace(/_/g, ' ')}</span>}
                        {r.feeds_step && <span className="rb-rec-step">Step {r.feeds_step}</span>}
                    </div>
                    {(r.business_impact || r.description) && (
                        <p className="rb-rec-detail">{r.business_impact || r.description}</p>
                    )}
                    <div className="rb-rec-meta">
                        {r.timeline && <span>{r.timeline}</span>}
                        {r.priority && <span>Priority: {r.priority}</span>}
                        {r.investment_level && <span>Investment: {r.investment_level}</span>}
                    </div>
                </li>
            ))}
        </ul>
    );
}
